/**
 * How today looks, and at what times — the one line a collapsed zone can show.
 *
 * Pure and import-free so `npm run test:ori` can exercise it without a browser
 * (the module it used to live in pulls in `config/brand`, which Node ESM will
 * not resolve). Same reason `placeSections` and friends are separate.
 */

export interface TimedEventLike {
  start_time?: string
  end_time?: string
  is_all_day?: boolean
  completed?: boolean
}

/** Minutes past midnight from "09:15" or "2026-09-08T09:15", else null. */
export function parseHHMM(value?: string): number | null {
  if (!value) return null
  const m = value.match(/(?:^|T)(\d{1,2}):(\d{2})/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

function hhmm(mins: number): string {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
}

/**
 * The day's shape, in one phrase, for a COLLAPSED zone header.
 *
 * Owner, 2026-09-08: *"I also want to peek into how the day is looking like and
 * at what times."* "Start here" deliberately shows one job, and the rest of the
 * day lives behind a collapsed zone whose header said only "3 of 5 tasks done ·
 * 0 of 2 habits done" — counts, no clock. So the times existed but cost a click
 * to see, and the page could not answer "what does today actually look like?".
 *
 * What it says, in order of what a person wants next:
 *   something still ahead   "next 14:30"    — the only time that changes plans
 *   nothing ahead           "7 today · 05:45–11:30"  — the shape that was
 *   nothing timed at all    null            — say nothing rather than "0"
 *
 * All-day rows carry no clock and are left out of the span on purpose;
 * completed ones are KEPT, because they are part of how the day looked.
 */
export function dayShapeWords(events: TimedEventLike[], nowMinutes: number): string | null {
  const starts: number[] = []
  for (const ev of events) {
    if (ev.is_all_day) continue
    const s = parseHHMM(ev.start_time)
    if (s !== null) starts.push(s)
  }
  if (starts.length === 0) return null
  starts.sort((a, b) => a - b)
  const ahead = starts.find(s => s > nowMinutes)
  if (ahead !== undefined) return `next ${hhmm(ahead)}`
  const first = starts[0]
  const last = starts[starts.length - 1]
  const span = first === last ? hhmm(first) : `${hhmm(first)}–${hhmm(last)}`
  return `${starts.length} today · ${span}`
}




export interface QueueRow {
  key: string
  /** The item behind the row, when the row knows it — so the row can OPEN
   *  it. Owner, 2026-09-11: the "then …" lines were "text sections with no
   *  links while Stretch has Open links". */
  id: string | null
  /** "10:00" — the row's own start, already formatted. */
  at: string
  title: string
  /** The row's one meta line, by the metadata contract (`taskMeta`). */
  meta: string
}

interface QueueEventLike extends TimedEventLike {
  id?: string
  item_id?: string | null
  title?: string
}

/**
 * What follows the current job, in the order it happens.
 *
 * Owner, 2026-09-08: *"a peek of the day (like a list of items I will do now
 * and these are the ones that would show up when this item is done)"*. So the
 * cut is AFTER THE HERO, not after the clock: on a day whose blocks have all
 * passed, "what is left ahead of now" is empty and answers nothing, while
 * "what comes after the thing you are being asked to do" still does.
 *
 * Completed rows are dropped (they are behind you, and "Today so far" has
 * them), all-day rows carry no clock, and the hero itself never appears in its
 * own queue.
 */
export function upNext(
  events: QueueEventLike[],
  { afterMinutes, excludeItemId, limit = 3 }:
  { afterMinutes: number | null; excludeItemId?: string | null; limit?: number },
): QueueRow[] {
  const rows: Array<{ start: number; row: QueueRow }> = []
  for (const ev of events) {
    if (ev.is_all_day || ev.completed) continue
    if (excludeItemId != null && ev.item_id != null && String(ev.item_id) === String(excludeItemId)) continue
    const start = parseHHMM(ev.start_time)
    if (start === null) continue
    if (afterMinutes !== null && start < afterMinutes) continue
    const title = (ev.title || '').trim()
    if (!title) continue
    rows.push({
      start,
      row: {
        key: String(ev.id ?? `${ev.start_time}:${title}`),
        id: ev.item_id != null ? String(ev.item_id) : null,
        at: hhmm(start), title,
        meta: taskMeta({ slot: ev.end_time ? `${ev.start_time}-${ev.end_time}` : ev.start_time }),
      },
    })
  }
  rows.sort((a, b) => a.start - b.start)
  return rows.slice(0, limit).map(r => r.row)
}


interface CandidateLike {
  task_id?: string
  title?: string
  scheduled_time_slot?: string | null
  estimated_duration?: number | null
}

/**
 * The next few things the card itself would offer, in ITS order.
 *
 * Owner, 2026-09-08: *"these are the ones that would show up when this item is
 * done"* — which is the candidate rotation, not the clock. A first attempt cut
 * the list at the hero's start time and came back EMPTY on a real day: the two
 * tasks still open were scheduled 07:00 and 07:45, the hero 09:15, so "later
 * than the hero" hid all the remaining work. Order here is the selector's, so
 * this is literally what "Not this one →" would walk to.
 *
 * The time is shown when the row has one and is never used to sort.
 */
export function queueAfterHero(
  candidates: CandidateLike[], startIndex: number, limit = 3,
): QueueRow[] {
  const out: QueueRow[] = []
  for (const c of candidates.slice(Math.max(0, startIndex))) {
    const title = (c.title || '').trim()
    if (!title) continue
    const start = parseHHMM(c.scheduled_time_slot ?? undefined)
    out.push({
      key: String(c.task_id ?? title),
      id: c.task_id != null ? String(c.task_id) : null,
      at: start === null ? '' : hhmm(start),
      title,
      meta: taskMeta({ durationMinutes: c.estimated_duration, slot: c.scheduled_time_slot }),
    })
    if (out.length >= limit) break
  }
  return out
}


/** Minutes past midnight for a slot like "14:15-15:00", else null. */
export function slotStartMinutes(slot?: string | null): number | null {
  return parseHHMM(slot ?? undefined)
}

/**
 * Rows ordered by when they START, earliest first; rows with no readable
 * start keep their relative order at the end. Stable, so a tie never
 * reshuffles between reads.
 *
 * Why this exists (2026-09-11): the Start-here selector's "scheduled about
 * now" rule matched three blocks at once and kept POOL order inside the rule,
 * which was the focus plan's order — so the hero read "Practice telling a
 * story · 15:45" with "then 14:15 …" beneath it, seven minutes before 14:15.
 * Inside that rule the clock is the only honest tie-break.
 */
export function orderByStart<T>(rows: T[], slotOf: (row: T) => string | null | undefined): T[] {
  return rows
    .map((row, i) => ({ row, i, at: slotStartMinutes(slotOf(row)) }))
    .sort((a, b) => {
      if (a.at === null && b.at === null) return a.i - b.i
      if (a.at === null) return 1
      if (b.at === null) return -1
      return a.at - b.at || a.i - b.i
    })
    .map(x => x.row)
}


/**
 * `items` in the order of `idOrder`, then anything not named, in its own order.
 *
 * Focus mode (2026-09-11) walked the day's items sorted by PRIORITY while the
 * Do card ranked them with `selectCandidates` — so the two surfaces disagreed
 * on what was "first". Focus mode now takes the Do card's ranking as a list of
 * ids and orders itself by it; habits and anything the ranking did not name
 * follow, untouched.
 */
export function orderByIds<T>(items: T[], idOrder: string[], idOf: (item: T) => string): T[] {
  const rank = new Map(idOrder.map((id, i) => [id, i]))
  return items
    .map((item, i) => ({ item, i, r: rank.get(idOf(item)) }))
    .sort((a, b) => {
      if (a.r === undefined && b.r === undefined) return a.i - b.i
      if (a.r === undefined) return 1
      if (b.r === undefined) return -1
      return a.r - b.r || a.i - b.i
    })
    .map(x => x.item)
}

// ── The metadata contract (North Star rule 4, 2026-09-11) ─────────────────
//
// Owner: *"Metadata follows rules, not availability. If scheduled tasks
// normally show `30 min · 2:15–3:00 PM`, every scheduled task follows that
// format. If duration is unknown, deliberately show just the time — or nothing
// — according to the component contract. Inconsistency should never come from
// whatever data happened to be returned."*
//
// One function builds the line for EVERY task row on /today: the hero, the
// "then" rows, the timeline rows, the resting rows. Duration first, then the
// clock range; either half may be absent; the join is always " · ".

/** "45 min", "1 hr", "1 hr 15 min" — or '' for nothing usable. */
export function formatDuration(mins?: number | null): string {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return ''
  const m = Math.round(mins)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`
}

/** Start and end minutes of a "HH:MM-HH:MM" (or "HH:MM") slot, or null. */
export function slotRange(slot?: string | null): { start: number; end: number | null } | null {
  const start = parseHHMM(slot ?? undefined)
  if (start === null) return null
  const raw = String(slot)
  if (raw.includes('T')) return { start, end: null }
  const parts = raw.split(/[-–]/)
  const end = parts.length > 1 ? parseHHMM(parts[1].trim()) : null
  return { start, end }
}

/** "14:15–15:00" from a slot, "14:15" from a bare start, '' from nothing.
 *  One en dash, no spaces — the same shape `dayShapeWords` already uses. */
export function slotRangeLabel(slot?: string | null): string {
  const r = slotRange(slot)
  if (!r) return ''
  return r.end === null || r.end === r.start ? hhmm(r.start) : `${hhmm(r.start)}–${hhmm(r.end)}`
}

/** The one meta line: "45 min · 14:15–15:00" / "45 min" / "14:15–15:00" / ''. */
export function taskMeta(i: { durationMinutes?: number | null; slot?: string | null }): string {
  return [formatDuration(i.durationMinutes), slotRangeLabel(i.slot)].filter(Boolean).join(' · ')
}

// ── The order contract (North Star rule 5, 2026-09-11) ────────────────────
//
// Owner: *"If Horton says 'then', those items must actually be in
// chronological/execution order. 3:00 PM cannot appear after 3:45 PM."* Live
// that day the Do card read "then 15:45 … then 15:00": the queue followed the
// selector's RULE order, and the two blocks had matched different rules.
//
// The walk the whole page shares — hero, "then" rows, "Not this one →" and
// focus mode — is: the leads the person made themself (already started,
// pinned, a deadline) in the selector's order, then EVERYTHING ELSE by the
// clock, and what has no clock after that in the selector's order.

/**
 * `sticky` names the entries that keep their ranked position ahead of the
 * clock. Everything else is ordered by start time; rows with no start keep
 * their relative order at the end.
 */
export function executionWalk<T>(
  ranked: T[],
  slotOf: (row: T) => string | null | undefined,
  sticky: (row: T) => boolean = () => false,
): T[] {
  const leads = ranked.filter(sticky)
  const rest = ranked.filter(r => !sticky(r))
  return [...leads, ...orderByStart(rest, slotOf)]
}

/**
 * The first pair of timed rows whose windows overlap, or null. A planner that
 * draws two blocks over the same minutes and says nothing is not trustworthy
 * (rule 5: "Conflicting schedules should be surfaced as a problem, not
 * silently rendered"). Rows with no end are taken as points and never overlap.
 */
export function slotOverlap<T>(
  rows: T[], slotOf: (row: T) => string | null | undefined,
): { a: T; b: T; at: string } | null {
  const timed = rows
    .map(row => ({ row, r: slotRange(slotOf(row)) }))
    .filter((x): x is { row: T; r: { start: number; end: number } } => !!x.r && x.r.end !== null && x.r.end > x.r.start)
    .sort((x, y) => x.r.start - y.r.start)
  for (let i = 1; i < timed.length; i++) {
    const prev = timed[i - 1], cur = timed[i]
    if (cur.r.start < prev.r.end) return { a: prev.row, b: cur.row, at: hhmm(cur.r.start) }
  }
  return null
}
